import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';

export interface ReviewMember {
  id: string;
  name: string;
  /** Phone / email line under the name. */
  subtitle?: string;
  /** Extra context on the right (pack, class time, city…). */
  detail?: string;
  /** Small warning chip, e.g. "No app" for push without a token. */
  badge?: string;
}

const PAGE = 100;

/**
 * "Review recipients" — everyone an audience matched, with a checkbox each.
 * Untick people to leave them out; the result is the list of EXCLUDED ids.
 * Shared by Push notifications and WhatsApp workflows.
 */
const AudienceReviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onApply: (excludedIds: string[]) => void;
  members: ReviewMember[] | null;
  loading?: boolean;
  initiallyExcluded: string[];
  accent?: string;
  title?: string;
  truncated?: boolean;
  /** Label for the confirm button, given the number selected. */
  applyLabel?: (selected: number) => string;
}> = ({ open, onClose, onApply, members, loading, initiallyExcluded, accent = '#2196f3', title = 'Review recipients', truncated, applyLabel }) => {
  const [excluded, setExcluded] = useState<Set<string>>(new Set(initiallyExcluded));
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'all' | 'selected' | 'excluded'>('all');
  const [limit, setLimit] = useState(PAGE);

  // Reset when opened, and again once the list arrives (the starting
  // selection may depend on it).
  useEffect(() => {
    if (open) {
      setExcluded(new Set(initiallyExcluded));
      setQuery('');
      setView('all');
      setLimit(PAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, members]);

  // A→Z by name; people with no name go last.
  const all = useMemo(
    () =>
      [...(members ?? [])]
        .map((m) => ({ ...m, name: (m.name ?? '').trim() || 'No name' }))
        .sort((a, b) => {
          const an = a.name === 'No name';
          const bn = b.name === 'No name';
          if (an !== bn) return an ? 1 : -1;
          return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
        }),
    [members],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((m) => {
      if (view === 'selected' && excluded.has(m.id)) return false;
      if (view === 'excluded' && !excluded.has(m.id)) return false;
      if (!q) return true;
      return [m.name, m.subtitle, m.detail].some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [all, query, view, excluded]);

  const selectedCount = all.filter((m) => !excluded.has(m.id)).length;
  const allFilteredSelected = filtered.length > 0 && filtered.every((m) => !excluded.has(m.id));

  const toggle = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
  };

  const setFiltered = (include: boolean) => {
    const next = new Set(excluded);
    filtered.forEach((m) => (include ? next.delete(m.id) : next.add(m.id)));
    setExcluded(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, height: '85vh' } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
        <Stack direction="row" alignItems="center" mb={1.5}>
          <Box flexGrow={1}>
            <Typography variant="h6" fontWeight={800}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              <b style={{ color: accent }}>{selectedCount}</b> of {all.length} selected · untick anyone who shouldn’t get it
            </Typography>
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search name, phone, email…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <ToggleButtonGroup
            size="small" exclusive value={view} onChange={(_, v) => { if (v) { setView(v); setLimit(PAGE); } }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.5 } }}
          >
            <ToggleButton value="all">All {all.length}</ToggleButton>
            <ToggleButton value="selected">Selected {selectedCount}</ToggleButton>
            <ToggleButton value="excluded">Excluded {all.length - selectedCount}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {loading || !members ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: accent }} /></Box>
        ) : (
          <>
            <Stack
              direction="row" alignItems="center"
              sx={{ px: 2, py: 0.8, position: 'sticky', top: 0, zIndex: 1, bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            >
              <Checkbox
                size="small"
                checked={allFilteredSelected}
                indeterminate={!allFilteredSelected && filtered.some((m) => !excluded.has(m.id))}
                onChange={() => setFiltered(!allFilteredSelected)}
                sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: accent } }}
              />
              <Typography variant="body2" fontWeight={700}>
                {allFilteredSelected ? 'Untick' : 'Tick'} all {query || view !== 'all' ? `${filtered.length} shown` : ''}
              </Typography>
            </Stack>
            {filtered.slice(0, limit).map((m) => {
              const on = !excluded.has(m.id);
              return (
                <Stack
                  key={m.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => toggle(m.id)}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.04)',
                    opacity: on ? 1 : 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  }}
                >
                  <Checkbox size="small" checked={on} sx={{ p: 0.5, '&.Mui-checked': { color: accent } }} />
                  <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: on ? `${accent}22` : '#eee', color: on ? accent : '#999' }}>
                    {(m.name || '?')[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} fontSize={14} noWrap sx={{ textDecoration: on ? 'none' : 'line-through' }}>
                      {m.name}
                    </Typography>
                    {m.subtitle && <Typography variant="caption" color="text.secondary" noWrap>{m.subtitle}</Typography>}
                  </Box>
                  {m.detail && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: 260, display: { xs: 'none', sm: 'block' } }} noWrap>
                      {m.detail}
                    </Typography>
                  )}
                  {m.badge && <Chip size="small" label={m.badge} sx={{ height: 20, fontSize: 11 }} />}
                </Stack>
              );
            })}
            {filtered.length > limit && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Button onClick={() => setLimit(limit + PAGE)} sx={{ fontWeight: 700, color: accent }}>
                  Show {Math.min(PAGE, filtered.length - limit)} more ({filtered.length - limit} left)
                </Button>
              </Box>
            )}
            {!filtered.length && (
              <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>Nobody here.</Typography>
            )}
            {truncated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1.5 }}>
                Showing the first {all.length} matches.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.8, borderTop: '1px solid rgba(0,0,0,0.07)' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {all.length - selectedCount > 0 ? `${all.length - selectedCount} will be left out` : 'Everyone is included'}
        </Typography>
        <Button onClick={onClose} sx={{ fontWeight: 700 }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!members || selectedCount === 0}
          onClick={() => onApply(all.filter((m) => excluded.has(m.id)).map((m) => m.id))}
          sx={{ bgcolor: accent, fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' } }}
        >
          {applyLabel ? applyLabel(selectedCount) : `Done · ${selectedCount} selected`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudienceReviewDialog;
